import { useEffect, useMemo, useState } from "react";
import { CalendarV2Event } from "@/shared/models/economic-calendar-v2/CalendarV2Event";
import { useGetCalendarV2Events } from "@/shared/api/economic-calendar-v2/useGetCalendarV2Events";

interface EconomicCalendarV2CountdownProps {
  onClick: (upcomingEvent: CalendarV2Event) => void;
}

const formatTimeFromMilliseconds = (timeInMillis: number) => ({
  hours: Math.floor(timeInMillis / 1000 / 60 / 60),
  minutes: Math.floor((timeInMillis / 1000 / 60) % 60),
  seconds: Math.floor((timeInMillis / 1000) % 60),
  totalInMillis: timeInMillis,
});

const computeUpcomingEvent = (
  events: CalendarV2Event[]
): CalendarV2Event | undefined => {
  if (!events || events.length === 0) return undefined;

  const now = new Date();

  const upcomingEvents = events
    .filter((event) => {
      const eventTime = new Date(event.occurs_at);
      return !isNaN(eventTime.getTime()) && eventTime.getTime() > now.getTime();
    })
    .sort(
      (a, b) =>
        new Date(a.occurs_at).getTime() - new Date(b.occurs_at).getTime()
    );

  return upcomingEvents.length > 0 ? upcomingEvents[0] : undefined;
};

const computeTimeToEvent = (event: CalendarV2Event): number => {
  const eventTime = new Date(event.occurs_at);
  if (isNaN(eventTime.getTime())) return 0;
  const diff = eventTime.getTime() - new Date().getTime();
  return diff < 0 ? 0 : diff;
};

const EconomicCalendarV2Countdown = (
  props: EconomicCalendarV2CountdownProps
) => {
  const { data: calendarEvents, isLoading } = useGetCalendarV2Events(new Date());

  const [upcomingEvent, setUpcomingEvent] = useState<CalendarV2Event>();
  const [timeToUpcomingEvent, setTimeToUpcomingEvent] = useState(0);

  const formattedTimeToUpcomingEvent = useMemo(
    () => formatTimeFromMilliseconds(timeToUpcomingEvent),
    [timeToUpcomingEvent]
  );

  useEffect(() => {
    if (!calendarEvents) return;
    setUpcomingEvent(computeUpcomingEvent(calendarEvents));
  }, [calendarEvents]);

  useEffect(() => {
    if (!upcomingEvent) return;

    setTimeToUpcomingEvent(computeTimeToEvent(upcomingEvent));

    const interval = setInterval(() => {
      const remaining = computeTimeToEvent(upcomingEvent);
      if (remaining <= 0) {
        setUpcomingEvent(computeUpcomingEvent(calendarEvents || []));
      } else {
        setTimeToUpcomingEvent(remaining);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [upcomingEvent, calendarEvents]);

  if (isLoading)
    return (
      <p className="text-gray-500 dark:text-gray-400 text-xs">Loading...</p>
    );

  return upcomingEvent ? (
    <div className="text-gray-700 dark:text-gray-300 flex flex-col items-center">
      <button
        className="rounded-none w-full text-1.5xs bg-white dark:bg-gray-900 hover:border-squawk-blue hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-600 dark:hover:text-gray-400  focus:outline-none"
        onClick={() => props.onClick(upcomingEvent)}
      >
        Next event:{" "}
        <span className="text-squawk-blue ">
          <span className="inline-block w-[2ch] text-center ">
            {formattedTimeToUpcomingEvent.hours.toString().padStart(2, "0")}
          </span>
          :
          <span className="inline-block w-[2ch] text-center ">
            {formattedTimeToUpcomingEvent.minutes.toString().padStart(2, "0")}
          </span>
          :
          <span className="inline-block w-[2ch] text-center ">
            {formattedTimeToUpcomingEvent.seconds.toString().padStart(2, "0")}
          </span>
        </span>
      </button>
    </div>
  ) : (
    <p className="text-sm text-gray-600 dark:text-gray-300 text-center">
      No upcoming events found.
    </p>
  );
};

export default EconomicCalendarV2Countdown;
